import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ArtistHeader from './components/ArtistHeader';
import ContentSectionCard from './components/ContentSectionCard';
import ArtistAnalytics from './components/ArtistAnalytics';
import EditorActions from './components/EditorActions';
import ValidationIndicators from './components/ValidationIndicators';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ArtistDetailEditorView = () => {
  const navigate = useNavigate();
  const { artistId } = useParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Mock artist data
  const artistData = {
    id: "artist_001",
    name: "Sarah Chen",
    type: "Visual",
    email: "sarah.chen@email.com",
    phone: "+1 (555) 123-4567",
    website: "https://sarahchen.art",
    profileImage: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
    status: "active",
    joinedDate: "March 15, 2024",
    lastModified: "2 hours ago",
    contentStatus: "pending",
    lastEditorAction: "Content review requested - 1 day ago",
    recentNotes: [
      {
        content: "Profile section looks great, but missing social media links for Instagram and LinkedIn.",
        timestamp: "2 hours ago",
        editor: "Mike Johnson",
        sections: ["Profile"]
      },
      {
        content: "Calendar events need better descriptions and featured images for upcoming shows.",
        timestamp: "1 day ago",
        editor: "Lisa Wang",
        sections: ["Calendar"]
      },
      {
        content: "Work portfolio is impressive, consider featuring the latest sculpture series.",
        timestamp: "3 days ago",
        editor: "Mike Johnson",
        sections: ["Work"]
      }
    ]
  };

  // Mock content sections data
  const contentSections = [
    {
      name: "Profile",
      completion: 85,
      hasErrors: false,
      basicInfo: true,
      socialLinksCount: 4,
      contactComplete: true,
      description: "Artist profile information and contact details",
      lastModified: "2 hours ago"
    },
    {
      name: "Promotion",
      completion: 60,
      hasErrors: true,
      description: "Splash screen, home screen images, and featured content",
      lastModified: "1 day ago"
    },
    {
      name: "About",
      completion: 90,
      hasErrors: false,
      description: "Artist biography, story, and booking information",
      lastModified: "3 hours ago"
    },
    {
      name: "Calendar",
      completion: 75,
      hasErrors: false,
      totalEvents: 12,
      upcomingEvents: 5,
      latestEvent: "Art Gallery Opening - Dec 20, 2024",
      description: "Upcoming events, exhibitions, and shows",
      lastModified: "5 hours ago"
    },
    {
      name: "Work",
      completion: 95,
      hasErrors: false,
      totalWorks: 28,
      featuredWorks: 6,
      lastUpdated: "1 day ago",
      description: "Portfolio of artworks, sculptures, and installations",
      lastModified: "1 day ago"
    },
    {
      name: "Shop",
      completion: 40,
      hasErrors: true,
      description: "Merchandise, prints, and collectible items",
      lastModified: "1 week ago"
    }
  ];

  // Mock analytics data
  const analyticsData = {
    profileViews: 15420,
    viewsGrowth: 12.5,
    uniqueVisitors: 8930,
    visitorsGrowth: 8.2,
    engagementRate: 24.8,
    engagementChange: 2.1,
    overallCompletion: 74,
    completionGrowth: 15.3,
    recentActivity: [
      {
        icon: "Edit",
        description: "Updated work portfolio with 3 new sculptures",
        timestamp: "2 hours ago",
        section: "Work"
      },
      {
        icon: "Calendar",
        description: "Added upcoming gallery opening event",
        timestamp: "5 hours ago",
        section: "Calendar"
      },
      {
        icon: "User",
        description: "Updated social media links",
        timestamp: "1 day ago",
        section: "Profile"
      },
      {
        icon: "FileText",
        description: "Modified artist biography",
        timestamp: "2 days ago",
        section: "About"
      }
    ]
  };

  // Mock validation data
  const validationData = {
    overallScore: 78,
    errorCount: 3,
    warningCount: 7,
    passedCount: 24,
    sectionValidation: [
      { name: "Profile", icon: "User", errors: 0, warnings: 1, completeness: 85 },
      { name: "Promotion", icon: "Megaphone", errors: 2, warnings: 1, completeness: 60 },
      { name: "About", icon: "FileText", errors: 0, warnings: 0, completeness: 90 },
      { name: "Calendar", icon: "Calendar", errors: 0, warnings: 2, completeness: 75 },
      { name: "Work", icon: "Briefcase", errors: 0, warnings: 1, completeness: 95 },
      { name: "Shop", icon: "ShoppingBag", errors: 1, warnings: 2, completeness: 40 }
    ],
    detailedIssues: [
      {
        type: "error",
        severity: "high",
        title: "Missing Required Field",
        description: "Splash screen image is required for mobile app promotion section",
        section: "Promotion",
        field: "splashImage",
        suggestion: "Upload a 1080x1920 pixel image for the splash screen"
      },
      {
        type: "error",
        severity: "medium",
        title: "Invalid Image Dimensions",
        description: "Home screen image does not meet the recommended 16:9 aspect ratio",
        section: "Promotion",
        field: "homeImage",
        suggestion: "Resize image to 1920x1080 pixels or similar 16:9 ratio"
      },
      {
        type: "warning",
        severity: "low",
        title: "Incomplete Social Links",
        description: "LinkedIn profile link is missing from social media section",
        section: "Profile",
        field: "linkedinUrl",
        suggestion: "Add LinkedIn profile URL to improve professional visibility"
      },
      {
        type: "warning",
        severity: "medium",
        title: "Low Shop Content",
        description: "Only 2 out of 7 product categories have items listed",
        section: "Shop",
        field: "products",
        suggestion: "Add more merchandise options to increase revenue potential"
      }
    ],
    qualityMetrics: [
      { name: "Content", score: 82 },
      { name: "Images", score: 75 },
      { name: "SEO", score: 68 },
      { name: "Mobile", score: 88 }
    ]
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleEditSection = (sectionName) => {
    console.log(`Editing section: ${sectionName}`);
    // In a real app, this would open a modal or navigate to edit page
  };

  const handlePreviewApp = () => {
    navigate('/mobile-app-preview', { state: { artistId: artistData?.id } });
  };

  const handleEditProfile = () => {
    navigate('/artist-content-management', { state: { artistId: artistData?.id, section: 'profile' } });
  };

  const handleSaveNote = (note) => {
    console.log('Saving note:', note);
    // In a real app, this would save to backend
  };

  const handleApproveContent = (sections) => {
    console.log('Approving sections:', sections);
    // In a real app, this would update content status
  };

  const handleRequestChanges = (sections) => {
    console.log('Requesting changes for sections:', sections);
    // In a real app, this would send change requests
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="editor" userName="Mike Johnson" userEmail="mike.johnson@idartists.com" />
        <Sidebar userRole="editor" isCollapsed={isSidebarCollapsed} />
        <main className={`pt-16 transition-medium ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading artist details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="editor" userName="Mike Johnson" userEmail="mike.johnson@idartists.com" />
      <Sidebar 
        userRole="editor" 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main className={`pt-16 transition-medium ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="p-6">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center space-x-2 text-sm text-text-secondary mb-6">
            <button 
              onClick={() => navigate('/editor-dashboard')}
              className="hover:text-accent transition-smooth"
            >
              Dashboard
            </button>
            <Icon name="ChevronRight" size={14} />
            <button 
              onClick={() => navigate('/artist-content-management')}
              className="hover:text-accent transition-smooth"
            >
              Artists
            </button>
            <Icon name="ChevronRight" size={14} />
            <span className="text-text-primary font-medium">{artistData?.name}</span>
          </div>

          {/* Artist Header */}
          <div className="mb-6">
            <ArtistHeader 
              artist={artistData}
              onEdit={handleEditProfile}
              onPreview={handlePreviewApp}
            />
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center space-x-1 mb-6 border-b border-border">
            {[
              { id: 'overview', label: 'Content Overview', icon: 'LayoutGrid' },
              { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
              { id: 'validation', label: 'Validation', icon: 'CheckCircle' },
              { id: 'actions', label: 'Editor Actions', icon: 'Settings' }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium border-b-2 transition-smooth ${
                  activeTab === tab?.id
                    ? 'text-accent border-accent' :'text-text-secondary border-transparent hover:text-text-primary hover:border-border'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                {/* Content Sections Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {contentSections?.map((section, index) => (
                    <ContentSectionCard
                      key={index}
                      section={section}
                      onEdit={handleEditSection}
                    />
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="bg-surface border border-border rounded-lg p-6 elevation-1">
                  <h2 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={handlePreviewApp}
                      className="justify-start"
                    >
                      Preview Mobile App
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      className="justify-start"
                    >
                      Export Content
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Share"
                      iconPosition="left"
                      className="justify-start"
                    >
                      Share Profile
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="History"
                      iconPosition="left"
                      className="justify-start"
                    >
                      View History
                    </Button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'analytics' && (
              <ArtistAnalytics analytics={analyticsData} />
            )}

            {activeTab === 'validation' && (
              <ValidationIndicators validationData={validationData} />
            )}

            {activeTab === 'actions' && (
              <EditorActions
                artist={artistData}
                onSaveNote={handleSaveNote}
                onApproveContent={handleApproveContent}
                onRequestChanges={handleRequestChanges}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ArtistDetailEditorView;